import type { ReportStrategyResponse } from "../types/reports";
import type {
  ComposeOutputMode,
  PineParseIssue,
  ReportStrategyPineFields,
  ReportStrategyPineParseResult,
  StrategyActivePineSummary,
} from "../types/reportStrategyPine";
import { getEffectiveInjectReadiness, isIndicatorPineScript } from "./pineIrUi";

export function mergeStrategyWithParseSummary(
  strategy: ReportStrategyResponse
): ReportStrategyResponse & ReportStrategyPineFields {
  const ps = strategy.pine_parse_summary;
  if (!ps) return strategy;
  return {
    ...strategy,
    pine_parse_status: ps.pine_parse_status ?? strategy.pine_parse_status,
    pine_script_kind: ps.pine_script_kind ?? strategy.pine_script_kind,
    pine_version: ps.pine_version ?? strategy.pine_version,
    inject_readiness: ps.inject_readiness ?? strategy.inject_readiness,
    exit_hook_readiness: ps.exit_hook_readiness ?? strategy.exit_hook_readiness,
  };
}

export function resolveDefaultComposeMode(
  strategyId: string,
  strategy: ReportStrategyPineFields,
  parsed?: ReportStrategyPineParseResult | null
): ComposeOutputMode {
  if (isIndicatorPineScript(strategy, parsed)) {
    return "append_policy_to_indicator";
  }
  const inject =
    getEffectiveInjectReadiness(strategy, parsed) ??
    strategy.pine_parse_summary?.inject_readiness ??
    strategy.inject_readiness;
  if (inject === "ready" || inject === "needs_review") {
    return "merge_into_strategy";
  }
  return "separate_indicator";
}

export function mergeComposeAllowed(
  strategy: ReportStrategyPineFields,
  parsed?: ReportStrategyPineParseResult | null
): boolean {
  if (isIndicatorPineScript(strategy, parsed)) return false;
  const inject =
    getEffectiveInjectReadiness(strategy, parsed) ??
    strategy.pine_parse_summary?.inject_readiness;
  return inject === "ready" || inject === "needs_review";
}

export function activePineProvenanceMismatch(
  active?: StrategyActivePineSummary | null,
  currentPolicyVersionId?: string | null,
  scriptKind: "indicator" | "strategy" = "strategy"
): string | null {
  if (!active?.last_compose_policy_version_ids?.length || !currentPolicyVersionId) return null;
  if (active.last_compose_policy_version_ids.includes(currentPolicyVersionId)) return null;
  const label = active.last_compose_policy_label?.trim() || "a previous policy version";
  const source = scriptKind === "indicator" ? "indicator" : "strategy";
  return `This active TradingView Pinescript is composed from your ${source} and ${label}. This is not your latest policy version. Re-compose with the current policy version to update.`;
}

export function parseIssuesFromSummary(strategy: ReportStrategyResponse): PineParseIssue[] {
  return strategy.pine_parse_summary?.parse_issues_top ?? [];
}
